import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";
import { LinkGate } from "@/components/common/LinkGate";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import ComingSoon from "@/pages/ComingSoon";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DataSecurity from "@/pages/DataSecurity";
import DeleteAccount from "@/pages/DeleteAccount";
import ChannelInvite from "@/pages/ChannelInvite";
import InviteLanding from "@/pages/InviteLanding";
import { GATE_ENABLED, gateEnabled } from "@/config";
import { usePublicConfig, useGated } from "@/hooks/usePublicConfig";

import AppShell from "@/pages/app/AppShell";
import ChatsPage from "@/pages/app/ChatsPage";
import BloqsPage from "@/pages/app/BloqsPage";
import CirclesPage from "@/pages/app/CirclesPage";
import ChannelsPage from "@/pages/app/ChannelsPage";
import ShieldPage from "@/pages/app/ShieldPage";
import ProfilePage from "@/pages/app/ProfilePage";
import RequestsPage from "@/pages/app/RequestsPage";
import NotificationsPage from "@/pages/app/NotificationsPage";
import ActivityPage from "@/pages/app/ActivityPage";
import SearchPage from "@/pages/app/SearchPage";
import MembershipPage from "@/pages/app/MembershipPage";
import ReferralsPage from "@/pages/app/ReferralsPage";
import WalletPage from "@/pages/app/WalletPage";
import SettingsPage from "@/pages/app/SettingsPage";
import CreatorPage from "@/pages/app/CreatorPage";
import CreatorMemberships from "@/pages/app/CreatorMemberships";
import TicketReturn from "@/pages/app/TicketReturn";
import StickerStudio from "@/pages/app/StickerStudio";
import PackView from "@/pages/app/PackView";
import AssistantPage from "@/pages/app/AssistantPage";
import DiscoverPage from "@/pages/app/DiscoverPage";
import HelpCenterPage from "@/pages/app/HelpCenterPage";
import MySpacesPage from "@/pages/app/MySpacesPage";
import PublicReputation from "@/pages/PublicReputation";

// Admin console lives in its own lazy chunks so the public bundle carries no admin UI or route strings (bfb_3655c1a6).
const AdminRoutes = lazy(() => import("@/pages/admin/AdminRoutes"));

// Runtime root gate — un-gates the moment the backend reports launched (GET /config/public),
// with no rebuild. Falls back to the build-time GATE_ENABLED until public config loads.
function RootGate() {
  const cfg = usePublicConfig();
  const gated = cfg == null ? GATE_ENABLED : gateEnabled(cfg);
  return gated ? <ComingSoon /> : <Landing />;
}

// Pre-launch lockout: while gated (private beta) any attempt to reach account creation is bounced
// to the waitlist so nobody new gets into the live app. Existing team/testers still use /login.
// Any invite/referral code on the URL is carried through to the waitlist so the inviting beta
// tester still gets credited. Auto-opens at launch via the runtime launched flag.
function WaitlistGate({ children }) {
  const gated = useGated();
  const [sp] = useSearchParams();
  if (gated) {
    const code = sp.get("code") || sp.get("invite") || sp.get("invite_code") || sp.get("ref");
    return <Navigate to={code ? `/?invite=${encodeURIComponent(code)}` : "/"} replace />;
  }
  return children;
}

// Legacy /app/circles/:id → /app/bloqs/:id (Circles were renamed to BLOQs; preserves query for Stripe returns).
function LegacyBloqRedirect() {
  const { bloqId } = useParams();
  const [sp] = useSearchParams();
  const qs = sp.toString();
  return <Navigate to={`/app/bloqs/${bloqId}${qs ? `?${qs}` : ""}`} replace />;
}


function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <LinkGate />
          <Routes>
            {/* Root: gated Coming Soon lander until launched (runtime, no rebuild), else the landing page. */}
            <Route path="/" element={<RootGate />} />

            {/* Lander preview (compare all 4 variants with the switcher). */}
            <Route path="/lander/:variant" element={<ComingSoon preview />} />

            {/* Messenger stays reachable for team + testers pre-launch — but account CREATION is
                locked behind the waitlist while gated (see WaitlistGate). */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<WaitlistGate><Signup /></WaitlistGate>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/data-security" element={<DataSecurity />} />
            <Route path="/delete-account" element={<DeleteAccount />} />

            {/* Public channel share/deep-link — funnels into waitlist (pre-launch) or signup (post-launch). */}
            <Route path="/c/:handle" element={<ChannelInvite />} />
            <Route path="/i/:code" element={<InviteLanding />} />

            {/* Public trust profile (Lane D) — no auth, shareable + viral CTA. */}
            <Route path="/u/:handle" element={<PublicReputation />} />

            {/* Lane B — fan memberships hub + Stripe checkout return target ({origin}/creators). Self-guards auth. */}
            <Route path="/creators" element={<CreatorMemberships />} />

            {/* Lane B — ticket checkout return target ({origin}/bloqs). Bare hits fall through to /app/bloqs. */}
            <Route path="/circles" element={<TicketReturn />} />

            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/app/chats" replace />} />
              <Route path="chats" element={<ChatsPage />} />
              <Route path="chats/:chatId" element={<ChatsPage />} />
              <Route path="bloqs" element={<BloqsPage />} />
              <Route path="bloqs/:bloqId" element={<BloqsPage />} />
              <Route path="circles" element={<CirclesPage />} />
              {/* Legacy community deep links (/app/circles/:id from old refs, emails, Stripe) → BLOQs */}
              <Route path="circles/:bloqId" element={<LegacyBloqRedirect />} />
              <Route path="channels" element={<ChannelsPage />} />
              <Route path="channels/:channelId" element={<ChannelsPage />} />
              <Route path="shield" element={<ShieldPage />} />
              <Route path="assistant" element={<AssistantPage />} />
              <Route path="discover" element={<DiscoverPage />} />
              <Route path="requests" element={<RequestsPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="activity" element={<ActivityPage />} />
              <Route path="search" element={<SearchPage />} />
              <Route path="membership" element={<MembershipPage />} />
              <Route path="referrals" element={<ReferralsPage />} />
              <Route path="wallet" element={<WalletPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="creator" element={<CreatorPage />} />
              <Route path="creators/:userId" element={<CreatorMemberships />} />
              <Route path="creator/stickers" element={<StickerStudio />} />
              <Route path="stickers/:packId" element={<PackView />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="help" element={<HelpCenterPage />} />
              <Route path="spaces" element={<MySpacesPage />} />
            </Route>

            <Route path="/admin/*" element={<Suspense fallback={null}><AdminRoutes /></Suspense>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          theme="dark"
          position="top-center"
          toastOptions={{
            style: {
              background: "#111623",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#fff",
            },
          }}
        />
      </AuthProvider>
    </div>
  );
}

export default App;
